import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  updateDoc,
  setDoc,
  increment,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { isReferralPayoutDoc } from '@/lib/referralLedger';

/**
 * Check if referral system is enabled
 */
export const isReferralSystemEnabled = async () => {
  try {
    const settingsDoc = await getDoc(doc(db, 'settings', 'referralSystem'));
    if (settingsDoc.exists()) {
      return settingsDoc.data().enabled !== false; // Default to true if not set
    }
    return true; // Default to enabled if setting doesn't exist
  } catch (error) {
    console.error('Error checking referral system status:', error);
    return true; // Default to enabled on error
  }
};

/**
 * Generate a unique referral code for a user
 * @param {string} userId - The user ID
 * @returns {Promise<string>} The generated referral code
 */
export const generateReferralCode = async (userId) => {
  try {
    // Generate a 6-character alphanumeric code
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    
    // Check if code already exists
    const existingCodeQuery = query(
      collection(db, 'referral_codes'),
      where('code', '==', code)
    );
    const existingCodeSnapshot = await getDocs(existingCodeQuery);
    
    if (!existingCodeSnapshot.empty) {
      // If code exists, generate a new one recursively
      return await generateReferralCode(userId);
    }
    
    // Create the referral code document
    await addDoc(collection(db, 'referral_codes'), {
      userId,
      code,
      isActive: true,
      createdAt: serverTimestamp(),
      usageCount: 0,
      totalEarnings: 0.00
    });
    
    console.log(`✅ Generated referral code ${code} for user ${userId}`);
    return code;
    
  } catch (error) {
    console.error('❌ Error generating referral code:', error);
    throw error;
  }
};

/**
 * Get user's referral code
 * @param {string} userId - The user ID
 * @returns {Promise<string|null>} The user's referral code or null
 */
export const getUserReferralCode = async (userId) => {
  try {
    const referralCodeQuery = query(
      collection(db, 'referral_codes'),
      where('userId', '==', userId),
      where('isActive', '==', true),
      limit(1)
    );
    
    const snapshot = await getDocs(referralCodeQuery);
    
    if (snapshot.empty) {
      // Generate a new code if none exists
      return await generateReferralCode(userId);
    }
    
    const codeDoc = snapshot.docs[0];
    return codeDoc.data().code;
    
  } catch (error) {
    console.error('❌ Error getting user referral code:', error);
    throw error;
  }
};

/**
 * Process a referral when a new user signs up
 * @param {string} referralCode - The referral code used
 * @param {string} newUserId - The new user's ID
 * @returns {Promise<boolean>} Success status
 */
export const processReferral = async (referralCode, newUserId) => {
  console.log(`🔍 DEBUG: processReferral function called with:`, { referralCode, newUserId });
  console.log(`🔍 DEBUG: Function entry point reached`);
  
  try {
    console.log(`🔍 Processing referral: code=${referralCode}, newUser=${newUserId}`);
    console.log(`🔍 DEBUG: Starting processReferral function`);
    
    // Find the referral code
    const codeQuery = query(
      collection(db, 'referral_codes'),
      where('code', '==', referralCode),
      where('isActive', '==', true),
      limit(1)
    );
    
    const codeSnapshot = await getDocs(codeQuery);
    console.log(`🔍 DEBUG: Found ${codeSnapshot.docs.length} referral codes matching ${referralCode}`);
    
    if (codeSnapshot.empty) {
      console.log('❌ Invalid referral code:', referralCode);
      console.log('🔍 DEBUG: No referral codes found in database');
      
      // Debug: Check all referral codes in the database
      console.log('🔍 DEBUG: Checking all referral codes in database...');
      const allCodesQuery = query(collection(db, 'referral_codes'));
      const allCodesSnapshot = await getDocs(allCodesQuery);
      console.log(`🔍 DEBUG: Found ${allCodesSnapshot.docs.length} total referral codes in database`);
      allCodesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        console.log(`🔍 DEBUG: Code: ${data.code}, Active: ${data.isActive}, User: ${data.userId}`);
      });
      
      return false;
    }
    
    const codeDoc = codeSnapshot.docs[0];
    const codeData = codeDoc.data();
    const referrerId = codeData.userId;
    
    // Check if user is trying to refer themselves
    if (referrerId === newUserId) {
      console.log('❌ User cannot refer themselves');
      return false;
    }
    
    // Check if referral already exists
    const existingReferralQuery = query(
      collection(db, 'referrals'),
      where('referrerId', '==', referrerId),
      where('referredId', '==', newUserId),
      limit(1)
    );
    
    const existingReferralSnapshot = await getDocs(existingReferralQuery);
    
    if (!existingReferralSnapshot.empty) {
      console.log('❌ Referral already exists');
      return false;
    }
    
    // Get the new user's profile to get their stage name
    let stageName = 'New Artist';
    try {
      const profileDoc = await getDoc(doc(db, 'profiles', newUserId));
      if (profileDoc.exists()) {
        const profileData = profileDoc.data();
        stageName = profileData.artist_name || profileData.karaoke_name || 'New Artist';
      }
    } catch (profileError) {
      console.log('🔍 Could not fetch user profile, using default name');
    }
    
    // Create the referral relationship
    const referralData = {
      referrerId,
      referredId: newUserId,
      referredStageName: stageName, // Store the stage name
      referralCode,
      createdAt: serverTimestamp(),
      status: 'active',
      signupStage: 'signed_up', // Start with signed up
      hasAddedSongs: false,
      hasGoneLive: false,
      hasCompletedRequests: false,
      totalEarnings: 0.00,
      totalPayouts: 0.00,
      pendingEarnings: 0.00
    };
    
    console.log(`🔍 DEBUG: Creating referral document with data:`, referralData);
    const referralRef = await addDoc(collection(db, 'referrals'), referralData);
    console.log(`🔍 DEBUG: Referral document created with ID: ${referralRef.id}`);
    
    // Update the referral code usage count
    await updateDoc(doc(db, 'referral_codes', codeDoc.id), {
      usageCount: codeData.usageCount + 1
    });
    
    console.log(`✅ Referral created: ${referralRef.id}`);
    
    // Send notification to referrer
    await sendReferralSignupNotification(referrerId, newUserId, referralCode);
    
    return true;
    
  } catch (error) {
    console.error('❌ Error processing referral:', error);
    console.error('❌ Error details:', error.message, error.code);
    console.error('❌ Full error object:', error);
    return false;
  } finally {
    console.log(`🔍 DEBUG: processReferral function completed`);
  }
};

/**
 * Record an earning from a referral
 * @param {string} referredUserId - The user who generated the earning
 * @param {number} transactionAmount - The original transaction amount
 * @param {string} transactionType - Type of transaction
 * @param {string} transactionId - Original transaction ID
 * @returns {Promise<boolean>} Success status
 */
export const recordReferralEarning = async (referredUserId, transactionAmount, transactionType, transactionId) => {
  try {
    // Check if referral system is enabled
    const enabled = await isReferralSystemEnabled();
    if (!enabled) {
      console.log('🔍 Referral system is disabled, skipping referral earning');
      return false;
    }
    
    // Use debugLog instead of console.log to ensure it appears in floating debug console
    window.debugLog?.('REFERRAL_EARNING', `Recording referral earning`, {
      user: referredUserId,
      amount: transactionAmount,
      type: transactionType
    });
    console.log(`🔍 Recording referral earning: user=${referredUserId}, amount=${transactionAmount}, type=${transactionType}`);
    
    // Find the referral relationship
    const referralQuery = query(
      collection(db, 'referrals'),
      where('referredId', '==', referredUserId),
      where('status', '==', 'active'),
      limit(1)
    );
    
    const referralSnapshot = await getDocs(referralQuery);
    
    if (referralSnapshot.empty) {
      console.log('❌ No active referral found for user:', referredUserId);
      console.log('🔍 Debug: Searched for referredId:', referredUserId, 'with status: active');
      return false; // No referral, no earning
    }
    
    console.log('✅ Found referral record for user:', referredUserId);
    
    const referralDoc = referralSnapshot.docs[0];
    const referralData = referralDoc.data();
    const referrerId = referralData.referrerId;
    
    console.log('🔍 Referral data:', {
      referralId: referralDoc.id,
      referrerId,
      currentTotalEarnings: referralData.totalEarnings,
      currentPendingEarnings: referralData.pendingEarnings
    });
    
    // Calculate earnings breakdown
    const platformFee = transactionAmount * 0.30; // 30% total platform fee
    const referrerCommission = transactionAmount * 0.10; // 10% to referrer
    const platformNet = transactionAmount * 0.20; // 20% to platform
    
    console.log('💰 Earnings calculation:', {
      transactionAmount,
      referrerCommission,
      platformFee,
      platformNet
    });
    
    // Create earning record
    const earningData = {
      referrerId,
      referredId: referredUserId,
      referralId: referralDoc.id,
      amount: referrerCommission,
      originalAmount: transactionAmount,
      platformFee,
      referrerCommission,
      platformNet,
      transactionType,
      transactionId,
      createdAt: serverTimestamp(),
      status: 'pending'
    };
    
    console.log('📝 Creating earning record:', earningData);
    const earningRef = await addDoc(collection(db, 'earnings'), earningData);
    console.log('✅ Earning record created with ID:', earningRef.id);
    
    // Update referral totals
    const newTotalEarnings = (referralData.totalEarnings || 0) + referrerCommission;
    const newPendingEarnings = (referralData.pendingEarnings || 0) + referrerCommission;
    
    console.log('🔄 Updating referral totals:', {
      oldTotalEarnings: referralData.totalEarnings,
      newTotalEarnings,
      oldPendingEarnings: referralData.pendingEarnings,
      newPendingEarnings
    });
    
    await updateDoc(doc(db, 'referrals', referralDoc.id), {
      totalEarnings: newTotalEarnings,
      pendingEarnings: newPendingEarnings
    });
    
    console.log('✅ Referral totals updated successfully');
    
    // Update referral code totals
    const codeQuery = query(
      collection(db, 'referral_codes'),
      where('userId', '==', referrerId),
      where('isActive', '==', true),
      limit(1)
    );
    
    const codeSnapshot = await getDocs(codeQuery);
    if (!codeSnapshot.empty) {
      const codeDoc = codeSnapshot.docs[0];
      const codeData = codeDoc.data();
      await updateDoc(doc(db, 'referral_codes', codeDoc.id), {
        totalEarnings: codeData.totalEarnings + referrerCommission
      });
    }
    
    console.log(`✅ Referral earning recorded: ${earningRef.id}, amount: $${referrerCommission}`);
    return true;
    
  } catch (error) {
    console.error('❌ Error recording referral earning:', error);
    throw error;
  }
};

/**
 * Get user's referral statistics
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} Referral statistics
 */
export const getUserReferralStats = async (userId) => {
  try {
    console.log('🔍 Getting referral stats for user:', userId);
    
    // Get referral code - simplified query to avoid index issues
    const codeQuery = query(
      collection(db, 'referral_codes'),
      where('userId', '==', userId),
      limit(1)
    );
    
    let referralCode = null;
    try {
      const codeSnapshot = await getDocs(codeQuery);
      referralCode = codeSnapshot.empty ? null : codeSnapshot.docs[0].data().code;
    } catch (indexError) {
      console.error('🔍 Error loading referral code:', indexError);
      console.log('🔍 Index not ready, checking for existing code with simple query...');
      // Fallback: try to get any code for this user without complex queries
      const simpleQuery = query(
        collection(db, 'referral_codes'),
        where('userId', '==', userId)
      );
      try {
        const simpleSnapshot = await getDocs(simpleQuery);
        referralCode = simpleSnapshot.empty ? null : simpleSnapshot.docs[0].data().code;
      } catch (simpleError) {
        console.error('🔍 Simple query also failed:', simpleError);
        console.log('🔍 Will generate new code...');
      }
    }
    
    // If no referral code exists, generate one
    if (!referralCode) {
      console.log('🔍 No referral code found, generating one...');
      referralCode = await generateReferralCode(userId);
    }
    
    // Get referrals made by this user - with fallback for index issues
    let referrals = [];
    try {
      console.log('🔍 getUserReferralStats: Searching for referrals with referrerId:', userId);
      const referralsQuery = query(
        collection(db, 'referrals'),
        where('referrerId', '==', userId)
      );
      const referralsSnapshot = await getDocs(referralsQuery);
      console.log('🔍 getUserReferralStats: Found', referralsSnapshot.docs.length, 'referral documents');
      
      // Debug: Log all referral documents to see what's in the collection
      if (referralsSnapshot.docs.length === 0) {
        console.log('🔍 No referrals found, checking all referrals in collection...');
        const allReferralsQuery = query(collection(db, 'referrals'));
        const allReferralsSnapshot = await getDocs(allReferralsQuery);
        console.log('🔍 Total referrals in collection:', allReferralsSnapshot.docs.length);
        allReferralsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          console.log('🔍 Referral doc:', doc.id, 'referrerId:', data.referrerId, 'referredId:', data.referredId);
        });
      }
      
      referrals = referralsSnapshot.docs.map(doc => {
        const data = doc.data();
        // Determine stage display based on signupStage
        let signupStageDisplay = 'Signed Up';
        let signupStageColor = 'yellow';
        let signupStageTooltip = 'They\'ve created an account. Next: Add songs to their catalog';
        
        switch (data.signupStage) {
          case 'signed_up':
            signupStageDisplay = 'Signed Up';
            signupStageColor = 'yellow';
            signupStageTooltip = 'They\'ve created an account. Next: Add songs to their catalog';
            break;
          case 'added_songs':
            signupStageDisplay = 'Building Catalog';
            signupStageColor = 'blue';
            signupStageTooltip = 'They\'ve added songs. Next: Go live to start earning';
            break;
          case 'gone_live':
            signupStageDisplay = 'Ready to Earn';
            signupStageColor = 'purple';
            signupStageTooltip = 'They\'ve gone live. Next: Complete their first song request';
            break;
          case 'completed_requests':
            signupStageDisplay = 'Fully Active';
            signupStageColor = 'green';
            signupStageTooltip = 'They\'ve completed requests and are earning! 🎉';
            break;
          default:
            signupStageDisplay = 'Signed Up';
            signupStageColor = 'yellow';
            signupStageTooltip = 'They\'ve created an account. Next: Add songs to their catalog';
        }
        
        return {
          id: doc.id,
          ...data,
          signupStageDisplay,
          signupStageColor,
          signupStageTooltip
        };
      });
      
      // Sort by createdAt in JavaScript (newest first)
      referrals.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(0);
        return bTime - aTime;
      });
    } catch (referralsError) {
      console.error('🔍 Error loading referrals:', referralsError);
      console.log('🔍 Referrals query failed, using empty array for now...');
      referrals = [];
    }
    
    // Get earnings for this user - with fallback for index issues
    let earnings = [];
    try {
      const earningsQuery = query(
        collection(db, 'earnings'),
        where('referrerId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const earningsSnapshot = await getDocs(earningsQuery);
      earnings = earningsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (earningsError) {
      console.error('🔍 Error loading earnings:', earningsError);
      console.log('🔍 Earnings query failed, using empty array for now...');
      earnings = [];
    }
    
    // Calculate totals
    const totalEarnings = earnings.reduce((sum, earning) => sum + earning.amount, 0);
    const pendingEarnings = earnings
      .filter(earning => earning.status === 'pending')
      .reduce((sum, earning) => sum + earning.amount, 0);
    const paidEarnings = earnings
      .filter(earning => earning.status === 'paid')
      .reduce((sum, earning) => sum + earning.amount, 0);
    
    const stats = {
      referralCode,
      totalReferrals: referrals.length,
      activeReferrals: referrals.filter(r => r.status === 'active').length,
      totalEarnings,
      pendingEarnings,
      paidEarnings,
      referrals,
      earnings
    };
    
    console.log('✅ Referral stats loaded successfully:', stats);
    return stats;
    
  } catch (error) {
    console.error('❌ Error getting user referral stats:', error);
    console.error('Error details:', error.code, error.message);
    
    // Return default stats instead of throwing
    return {
      referralCode: null,
      totalReferrals: 0,
      activeReferrals: 0,
      totalEarnings: 0,
      pendingEarnings: 0,
      paidEarnings: 0,
      referrals: [],
      earnings: []
    };
  }
};

/**
 * Request a payout
 * @param {string} userId - The user ID
 * @param {number} amount - The payout amount
 * @param {Object} paymentDetails - Payment method details
 * @returns {Promise<string>} Payout request ID
 */
export const requestPayout = async (userId, amount, paymentDetails) => {
  try {
    // Get user's pending earnings
    const earningsQuery = query(
      collection(db, 'earnings'),
      where('referrerId', '==', userId),
      where('status', '==', 'pending')
    );
    
    const earningsSnapshot = await getDocs(earningsQuery);
    const pendingEarnings = earningsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const totalPending = pendingEarnings.reduce((sum, earning) => sum + earning.amount, 0);
    
    if (amount > totalPending) {
      throw new Error('Payout amount exceeds pending earnings');
    }
    
    const normalizedPaymentDetails = {
      method: paymentDetails.method,
      accountNumber: String(paymentDetails.accountNumber || '').trim(),
      sortCode: String(paymentDetails.sortCode || paymentDetails.routingNumber || '').trim(),
      routingNumber: String(paymentDetails.routingNumber || paymentDetails.sortCode || '').trim(),
      email: String(paymentDetails.email || '').trim(),
    };

    // Persist payout details on artist profile so they don't have to re-enter each request.
    if (normalizedPaymentDetails.method === 'bank_transfer') {
      await setDoc(
        doc(db, 'profiles', userId),
        {
          account_number: normalizedPaymentDetails.accountNumber || null,
          sort_code: normalizedPaymentDetails.sortCode || null,
          payout_method: 'bank_transfer',
          payout_details_updated_at: serverTimestamp(),
        },
        { merge: true }
      );
    } else if (normalizedPaymentDetails.method === 'paypal') {
      await setDoc(
        doc(db, 'profiles', userId),
        {
          paypal_email: normalizedPaymentDetails.email || null,
          payout_method: 'paypal',
          payout_details_updated_at: serverTimestamp(),
        },
        { merge: true }
      );
    }

    const payoutCurrency = String(
      pendingEarnings.find((e) => e.currency)?.currency || 'GBP'
    ).toUpperCase();

    // Create payout request
    const payoutData = {
      userId,
      amount,
      currency: payoutCurrency,
      ledger: 'referral',
      status: 'pending',
      requestedAt: serverTimestamp(),
      paymentMethod: normalizedPaymentDetails.method,
      paymentDetails: {
        accountNumber: normalizedPaymentDetails.accountNumber,
        sortCode: normalizedPaymentDetails.sortCode,
        routingNumber: normalizedPaymentDetails.routingNumber,
        email: normalizedPaymentDetails.email
      },
      earningsIds: pendingEarnings.map((earning) => earning.id),
      earningsBreakdown: pendingEarnings.map(earning => ({
        earningId: earning.id,
        referralId: earning.referralId,
        referredId: earning.referredId,
        transactionId: earning.transactionId || null,
        amount: earning.amount,
        originalAmount: earning.originalAmount || null,
        currency: earning.currency || payoutCurrency,
        period: earning.createdAt?.toDate?.()?.toISOString?.()?.substring(0, 7) || 'unknown'
      }))
    };
    
    const payoutRef = await addDoc(collection(db, 'payouts'), payoutData);

    await Promise.all(pendingEarnings.map((earning) =>
      updateDoc(doc(db, 'earnings', earning.id), {
        status: 'requested',
        payoutId: payoutRef.id,
        requestedAt: serverTimestamp(),
      })
    ));
    
    console.log(`✅ Payout request created: ${payoutRef.id}, amount: $${amount}`);
    return payoutRef.id;
    
  } catch (error) {
    console.error('❌ Error requesting payout:', error);
    throw error;
  }
};

/**
 * Get user's payout history
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} Payout history
 */
export const getUserPayoutHistory = async (userId) => {
  try {
    const payoutsQuery = query(
      collection(db, 'payouts'),
      where('userId', '==', userId),
      orderBy('requestedAt', 'desc')
    );
    
    const snapshot = await getDocs(payoutsQuery);
    return snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(isReferralPayoutDoc);
    
  } catch (error) {
    console.error('❌ Error getting user payout history:', error);
    console.log('🔍 Payouts index not ready, returning empty array...');
    return []; // Return empty array instead of throwing error
  }
};

async function resolvePayoutEarningIds(payoutId, payout) {
  const listed = Array.isArray(payout.earningsIds) ? payout.earningsIds.filter(Boolean) : [];
  if (listed.length) return listed;

  const fromBreakdown = (payout.earningsBreakdown || [])
    .map((row) => row.earningId)
    .filter(Boolean);
  if (fromBreakdown.length) return fromBreakdown;

  const earningsQuery = query(
    collection(db, 'earnings'),
    where('referrerId', '==', payout.userId)
  );
  const snapshot = await getDocs(earningsQuery);
  return snapshot.docs
    .filter((d) => {
      const data = d.data();
      return data.payoutId === payoutId || data.status === 'requested' || data.status === 'pending';
    })
    .map((d) => d.id);
}

async function applyEarningStatusAndReferralTotals(earningIds, nextStatus, payoutId) {
  for (const earningId of earningIds) {
    const earningRef = doc(db, 'earnings', earningId);
    const earningSnap = await getDoc(earningRef);
    if (!earningSnap.exists()) continue;
    const earning = earningSnap.data();
    const previousStatus = earning.status;
    if (previousStatus === nextStatus) continue;

    await updateDoc(earningRef, {
      status: nextStatus,
      payoutId: nextStatus === 'pending' ? null : payoutId,
      ...(nextStatus === 'paid' ? { paidAt: serverTimestamp() } : {}),
      ...(nextStatus === 'pending' ? { requestedAt: null } : {}),
    });

    if (!earning.referralId) continue;
    const amount = Number(earning.amount || 0);
    if (!Number.isFinite(amount) || amount === 0) continue;

    const referralRef = doc(db, 'referrals', earning.referralId);
    if (nextStatus === 'paid' && previousStatus !== 'paid') {
      await updateDoc(referralRef, {
        pendingEarnings: increment(-amount),
        totalPayouts: increment(amount),
      });
    } else if (previousStatus === 'paid' && nextStatus !== 'paid') {
      await updateDoc(referralRef, {
        pendingEarnings: increment(amount),
        totalPayouts: increment(-amount),
      });
    }
  }
}

/**
 * Admin Console: mark a referral payout request as completed (paid).
 */
export const completeReferralPayout = async (payoutId, adminNote = '') => {
  const payoutRef = doc(db, 'payouts', payoutId);
  const payoutSnap = await getDoc(payoutRef);
  if (!payoutSnap.exists()) throw new Error('Payout request not found');
  const payout = payoutSnap.data();
  if (!isReferralPayoutDoc({ id: payoutId, ...payout })) {
    throw new Error('This is not a referral payout request');
  }
  if (payout.status === 'paid') return payoutId;

  const earningIds = await resolvePayoutEarningIds(payoutId, payout);
  await applyEarningStatusAndReferralTotals(earningIds, 'paid', payoutId);

  await updateDoc(payoutRef, {
    status: 'paid',
    paidAt: serverTimestamp(),
    completedAt: serverTimestamp(),
    adminNote: adminNote || null,
  });

  return payoutId;
};

/**
 * Admin Console: reject a referral payout request and return earnings to pending.
 */
export const rejectReferralPayout = async (payoutId, adminNote = '') => {
  const payoutRef = doc(db, 'payouts', payoutId);
  const payoutSnap = await getDoc(payoutRef);
  if (!payoutSnap.exists()) throw new Error('Payout request not found');
  const payout = payoutSnap.data();
  if (!isReferralPayoutDoc({ id: payoutId, ...payout })) {
    throw new Error('This is not a referral payout request');
  }
  if (payout.status === 'paid') {
    throw new Error('This payout has already been marked completed');
  }

  const earningIds = await resolvePayoutEarningIds(payoutId, payout);
  await applyEarningStatusAndReferralTotals(earningIds, 'pending', payoutId);

  await updateDoc(payoutRef, {
    status: 'rejected',
    rejectedAt: serverTimestamp(),
    adminNote: adminNote || null,
  });

  return payoutId;
};

/**
 * Admin Console: write missing referral earning rows from paid song requests.
 * Tips are never included. Dedupes on request / transaction id.
 */
export const syncMissingReferralEarnings = async (lines) => {
  if (!Array.isArray(lines) || lines.length === 0) return 0;
  let created = 0;

  for (const line of lines) {
    if (!line?.requestId || !line?.referrerId || !line?.referralId) continue;
    const amount = Number(line.referrer10);
    if (!Number.isFinite(amount) || amount <= 0) continue;

    const existingQuery = query(
      collection(db, 'earnings'),
      where('transactionId', '==', line.requestId),
      limit(1)
    );
    const existing = await getDocs(existingQuery);
    if (!existing.empty) continue;

    await addDoc(collection(db, 'earnings'), {
      referrerId: line.referrerId,
      referredId: line.referredId,
      referralId: line.referralId,
      amount,
      originalAmount: Number(line.songGross) || 0,
      platformFee: Number(line.songGross || 0) * 0.3,
      referrerCommission: amount,
      platformNet: Number(line.platform20) || 0,
      referredArtistShare: Number(line.referredArtist70) || 0,
      tipExcluded: Number(line.tipExcluded) || 0,
      transactionType: 'song_request',
      transactionId: line.requestId,
      songTitle: line.songTitle || null,
      currency: line.currency || 'GBP',
      createdAt: serverTimestamp(),
      source: 'admin_sync',
      status: 'pending',
    });

    await updateDoc(doc(db, 'referrals', line.referralId), {
      totalEarnings: increment(amount),
      pendingEarnings: increment(amount),
      hasCompletedRequests: true,
      signupStage: 'completed_requests',
    });
    created += 1;
  }

  return created;
};

/**
 * Update referral when user goes live
 * @param {string} userId - The user's ID
 * @returns {Promise<void>}
 */
export const updateReferralLiveStatus = async (userId) => {
  try {
    console.log(`🔍 Updating referral live status for user: ${userId}`);
    
    // Find the referral relationship - simplified query to avoid index issues
    const referralQuery = query(
      collection(db, 'referrals'),
      where('referredId', '==', userId),
      limit(1)
    );
    
    const referralSnapshot = await getDocs(referralQuery);
    console.log(`🔍 Found ${referralSnapshot.docs.length} referral records for user: ${userId}`);
    
    if (!referralSnapshot.empty) {
      const referralDoc = referralSnapshot.docs[0];
      const referralData = referralDoc.data();
      console.log(`🔍 Current referral data:`, referralData);
      
      // Update the referral to mark that they've gone live
      await updateDoc(doc(db, 'referrals', referralDoc.id), {
        hasGoneLive: true,
        signupStage: 'gone_live' // They're now live and accepting requests
      });
      
      console.log(`✅ Referral live status updated for user: ${userId} to 'gone_live'`);
    } else {
      console.log(`❌ No referral found for user: ${userId}`);
    }
    
  } catch (error) {
    console.error('❌ Error updating referral live status:', error);
  }
};

/**
 * Update referral when user completes requests
 * @param {string} userId - The user's ID
 * @param {number} earnings - The earnings from completed requests
 * @returns {Promise<void>}
 */
export const updateReferralCompletion = async (userId, earnings = 0) => {
  try {
    // Check if referral system is enabled
    const enabled = await isReferralSystemEnabled();
    if (!enabled) {
      console.log('🔍 Referral system is disabled, skipping referral completion update');
      return;
    }
    
    console.log(`🔍 Updating referral completion for user: ${userId}, earnings: ${earnings}`);
    
    // Find the referral relationship - simplified query to avoid index issues
    const referralQuery = query(
      collection(db, 'referrals'),
      where('referredId', '==', userId),
      limit(1)
    );
    
    const referralSnapshot = await getDocs(referralQuery);
    console.log(`🔍 Found ${referralSnapshot.docs.length} referral records for user: ${userId}`);
    
    if (!referralSnapshot.empty) {
      const referralDoc = referralSnapshot.docs[0];
      const referralData = referralDoc.data();
      console.log(`🔍 Current referral data:`, referralData);
      
      // Calculate referrer earnings (10% of platform fee)
      const referrerEarnings = earnings * 0.1; // 10% of total earnings
      console.log(`🔍 Calculated referrer earnings: ${referrerEarnings} (10% of ${earnings})`);
      
      // Update the referral to mark completion and add earnings
      await updateDoc(doc(db, 'referrals', referralDoc.id), {
        hasCompletedRequests: true,
        signupStage: 'completed_requests',
        totalEarnings: (referralData.totalEarnings || 0) + referrerEarnings,
        pendingEarnings: (referralData.pendingEarnings || 0) + referrerEarnings
      });
      
      // Create an earnings record for the referrer
      await addDoc(collection(db, 'earnings'), {
        referrerId: referralData.referrerId,
        referredId: userId,
        amount: referrerEarnings,
        status: 'pending',
        source: 'referral_commission',
        transactionType: 'song_request',
        transactionId: `bulk_${Date.now()}`, // Unique ID for bulk transactions
        createdAt: serverTimestamp(),
        description: `10% commission from ${referralData.referredStageName || 'referred artist'} - ${earnings > 0 ? `£${earnings.toFixed(2)} in song requests` : 'song requests completed'}`
      });
      
      console.log(`✅ Referral completion updated for user: ${userId}, referrer earnings: ${referrerEarnings}`);
    } else {
      console.log(`❌ No referral found for user: ${userId}`);
    }
    
  } catch (error) {
    console.error('❌ Error updating referral completion:', error);
  }
};

/**
 * Send notification to referrer when someone signs up via their referral
 * @param {string} referrerId - The referrer's user ID
 * @param {string} referredUserId - The new user's ID
 * @param {string} referralCode - The referral code used
 * @returns {Promise<void>}
 */
export const sendReferralSignupNotification = async (referrerId, referredUserId, referralCode) => {
  try {
    console.log(`🔔 Sending referral signup notification to referrer: ${referrerId}`);
    
    // Get the new user's profile to get their name
    const newUserProfile = await getDoc(doc(db, 'profiles', referredUserId));
    const newUserName = newUserProfile.exists() ? 
      (newUserProfile.data().artist_name || newUserProfile.data().karaoke_name || 'New Artist') : 
      'New Artist';
    
    // Check if competition is enabled
    const { isCompetitionEnabled } = await import('@/lib/competition');
    const competitionEnabled = await isCompetitionEnabled();
    
    // Check if referral system is enabled
    const referralSystemEnabled = await isReferralSystemEnabled();
    
    // Determine notification message based on which system is enabled
    let message;
    if (competitionEnabled && !referralSystemEnabled) {
      // Competition is ON, Refer is OFF - mention points only
      message = `${newUserName} signed up using your referral code ${referralCode}! You'll receive +1 point for each song they complete in the competition.`;
    } else if (competitionEnabled && referralSystemEnabled) {
      // Both are ON - mention both points and commissions
      message = `${newUserName} signed up using your referral code ${referralCode}! You'll receive +1 point for each song they complete in the competition, plus 10% commission on their earnings.`;
    } else {
      // Refer is ON (or both OFF) - keep original message about commissions
      message = `${newUserName} signed up using your referral code ${referralCode}! They'll need to add songs to start earning you commissions.`;
    }
    
    // Create notification data
    const notificationData = {
      title: '🎉 New Referral Signup!',
      message: message,
      type: 'system',
      targetAudience: 'specific',
      targetUserId: referrerId,
      targetUserIds: [referrerId],
      status: 'sent',
      recipientCount: 1,
      sentAt: serverTimestamp(),
      data: {
        referralCode,
        referredUserId,
        referredUserName: newUserName,
        signupStage: 'completed'
      },
      createdAt: serverTimestamp(),
      read: false
    };
    
    // Add notification to admin_notifications collection
    await addDoc(collection(db, 'admin_notifications'), notificationData);
    
    console.log(`✅ Referral signup notification sent to referrer: ${referrerId}`);
    
  } catch (error) {
    console.error('❌ Error sending referral signup notification:', error);
    // Don't throw error - notification failure shouldn't break signup
  }
};

/**
 * Update referral signup stage when user adds songs
 * @param {string} userId - The user's ID
 * @returns {Promise<void>}
 */
export const updateReferralSignupStage = async (userId) => {
  try {
    console.log(`🔍 Updating referral signup stage for user: ${userId}`);
    
    // Find the referral relationship - simplified query to avoid index issues
    const referralQuery = query(
      collection(db, 'referrals'),
      where('referredId', '==', userId),
      limit(1)
    );
    
    const referralSnapshot = await getDocs(referralQuery);
    console.log(`🔍 Found ${referralSnapshot.docs.length} referral records for user: ${userId}`);
    
    if (!referralSnapshot.empty) {
      const referralDoc = referralSnapshot.docs[0];
      const referralData = referralDoc.data();
      console.log(`🔍 Current referral data:`, referralData);
      
      // Update the referral to mark that they've added songs
      await updateDoc(doc(db, 'referrals', referralDoc.id), {
        hasAddedSongs: true,
        signupStage: 'added_songs' // They've added songs to their catalog
      });
      
      console.log(`✅ Referral signup stage updated for user: ${userId} to 'added_songs'`);
      
      // Send notification to referrer that their referral is now active
      try {
        await sendReferralActiveNotification(referralData.referrerId, userId, referralData.referralCode);
      } catch (notificationError) {
        console.error('Error sending notification:', notificationError);
      }
    } else {
      console.log(`❌ No referral found for user: ${userId}`);
    }
    
  } catch (error) {
    console.error('❌ Error updating referral signup stage:', error);
  }
};

/**
 * Send notification to referrer when their referred artist completes a song
 * @param {string} referrerId - The referrer's user ID
 * @param {string} referredUserId - The referred artist's ID
 * @param {string} songTitle - The title of the song that was completed
 * @returns {Promise<void>}
 */
export const sendReferralSongCompletionNotification = async (referrerId, referredUserId, songTitle) => {
  try {
    console.log(`🔔 Sending referral song completion notification to referrer: ${referrerId}`);
    
    // Get the referred artist's profile to get their name
    const referredUserProfile = await getDoc(doc(db, 'profiles', referredUserId));
    const referredUserName = referredUserProfile.exists() ? 
      (referredUserProfile.data().artist_name || referredUserProfile.data().karaoke_name || 'Your referral') : 
      'Your referral';
    
    // Create notification data with artist name prominently mentioned
    const notificationData = {
      title: '🎵 Referral Song Completed!',
      message: `${referredUserName} completed "${songTitle}"! You've earned +1 competition point.`,
      type: 'system',
      targetAudience: 'specific',
      targetUserId: referrerId,
      targetUserIds: [referrerId],
      status: 'sent',
      recipientCount: 1,
      sentAt: serverTimestamp(),
      data: {
        referredUserId,
        referredUserName,
        songTitle,
        pointsEarned: 1,
        eventType: 'referral_song_completed'
      },
      createdAt: serverTimestamp(),
      read: false
    };
    
    // Add notification to admin_notifications collection
    await addDoc(collection(db, 'admin_notifications'), notificationData);
    
    console.log(`✅ Referral song completion notification sent to referrer: ${referrerId}`);
    
  } catch (error) {
    console.error('❌ Error sending referral song completion notification:', error);
    // Don't throw error - notification failure shouldn't break the process
  }
};

/**
 * Send notification to referrer when their referral becomes active (adds songs)
 * @param {string} referrerId - The referrer's user ID
 * @param {string} referredUserId - The new user's ID
 * @param {string} referralCode - The referral code used
 * @returns {Promise<void>}
 */
export const sendReferralActiveNotification = async (referrerId, referredUserId, referralCode) => {
  try {
    console.log(`🔔 Sending referral active notification to referrer: ${referrerId}`);
    
    // Get the new user's profile to get their name
    const newUserProfile = await getDoc(doc(db, 'profiles', referredUserId));
    const newUserName = newUserProfile.exists() ? 
      (newUserProfile.data().artist_name || newUserProfile.data().karaoke_name || 'New Artist') : 
      'New Artist';
    
    // Create notification data
    const notificationData = {
      title: '🚀 Referral Now Active!',
      message: `${newUserName} has added songs and is now active! You'll start earning commissions on their transactions.`,
      type: 'system',
      targetAudience: 'specific',
      targetUserId: referrerId,
      targetUserIds: [referrerId],
      status: 'sent',
      recipientCount: 1,
      sentAt: serverTimestamp(),
      data: {
        referralCode,
        referredUserId,
        referredUserName: newUserName,
        signupStage: 'active'
      },
      createdAt: serverTimestamp(),
      read: false
    };
    
    // Add notification to admin_notifications collection
    await addDoc(collection(db, 'admin_notifications'), notificationData);
    
    console.log(`✅ Referral active notification sent to referrer: ${referrerId}`);
    
  } catch (error) {
    console.error('❌ Error sending referral active notification:', error);
    // Don't throw error - notification failure shouldn't break the process
  }
};
